/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: Admin
 * File     : Admin.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the Admin view and coordinating data fetching.
 *   Exports: Admin
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/icons-material/Delete
 *   - @mui/icons-material/Save
 *   - @mui/material
 *   - react
 *
 * Usage:
 *   Use in Router: <Route path="/admin" element={<Admin />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { Typography, Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, TextField, Button, Select, MenuItem, Grid, Divider, Stack, Snackbar, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { listUsers, updateUser, deleteUser, listClubs, listClubMembers, addClubMember, removeClubMember, createRandomPoll, getClubWebhook, putClubWebhook, getDbConfig, saveDbConfig, exportDb, importDb } from '../services/api';
import { api } from '../services/api';
import { getAnalyticsReport } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Admin = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState({});
  const [clubs, setClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState('');
  const [members, setMembers] = useState([]);
  const [newMember, setNewMember] = useState({ username: '', role: 'member' });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [webhookCfg, setWebhookCfg] = useState({ url: '', message: '' });
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [dbCfg, setDbCfg] = useState({ dialect: 'sqlite', databaseUrl: '', storage: '', ssl: false });
  const [savingDb, setSavingDb] = useState(false);
  const [randCfg, setRandCfg] = useState({ count: 4, genre: '', actor: '', leadOnly: false, director: '', attachClubId: '', title: '', publicPoll: true, duration: '24h' });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (user && (user.role === 'owner' || user.role === 'admin')) {
      listUsers().then(d => { const arr = d.users || []; setUsers(arr); if (!selectedUserId && arr.length) setSelectedUserId(arr[0].id); }).catch(() => {});
      listClubs().then(d => setClubs(d.clubs || [])).catch(() => setClubs([]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadMembers = async (clubId) => {
    if (!clubId) return;
    try {
      const { members } = await listClubMembers(clubId);
      setMembers(members || []);
    } catch (_) { setMembers([]); }
  };

  React.useEffect(() => {
    if (!selectedClubId) { setWebhookCfg({ url: '', message: '' }); return; }
    getClubWebhook(selectedClubId)
      .then((r) => setWebhookCfg({ url: r.webhookUrl || '', message: r.webhookMessage || '' }))
      .catch(() => setWebhookCfg({ url: '', message: '' }));
  }, [selectedClubId]);

  React.useEffect(() => {
    if (user?.role !== 'owner') return;
    getDbConfig().then(setDbCfg).catch(() => {});
  }, [user]);

  const handleField = (id, field, value) => {
    setUsers(prev => prev.filter(u => u && u.id).map(u => u.id === id ? { ...u, [field]: value } : u));
  };

  const handleSave = async (u) => {
    setSaving(s => ({ ...s, [u.id]: true }));
    try {
      const payload = {};
      if (u.email) payload.email = u.email;
      if (u._password) payload.password = u._password;
      if (u.role) payload.role = u.role;
      // Prevent current user from changing their own role from the Admin panel
      if (u.id === user.id && payload.role && payload.role !== user.role) {
        delete payload.role;
        if (!payload.email && !payload.password) {
          setSnack({ open: true, message: 'You cannot modify your own role.', severity: 'warning' });
          setSaving(s => ({ ...s, [u.id]: false }));
          return;
        }
      }
      // Disallow anyone other than the Owner themselves from changing the Owner's password
      if (u.role === 'owner' && u.id !== user.id && payload.password) {
        delete payload.password;
        setSnack({ open: true, message: 'Only the Owner can change their own password.', severity: 'warning' });
      }
      const updated = await updateUser(u.id, payload);
      // Merge any returned role/email changes back into local state
      if (updated) {
        setUsers(prev => prev.filter(x => x && x.id).map(x => x.id === u.id ? { ...x, ...updated } : x));
        setSnack({ open: true, message: 'User updated successfully', severity: 'success' });
      }
    } catch (e) {
      setSnack({ open: true, message: e?.response?.data?.error || 'Failed to update user', severity: 'error' });
    }
    setSaving(s => ({ ...s, [u.id]: false }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user? Users with created polls cannot be deleted.')) return;
    try {
      await deleteUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch (e) {}
  };

  if (!(user && (user.role === 'owner' || user.role === 'admin'))) return <Typography>Admin access required.</Typography>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Admin Panel</Typography>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
      <Grid container spacing={2}>
        <Grid item xs={12}>
      <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="h6" gutterBottom>Users</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Tier</TableCell>
              <TableCell>New Password</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.filter(u => u && u.id).map(u => (
              <TableRow key={u.id} hover onClick={() => setSelectedUserId(u.id)} selected={selectedUserId === u.id} sx={{ cursor: 'pointer' }}>
                <TableCell>{u.id}</TableCell>
                <TableCell>{u.username}</TableCell>
                <TableCell>
                  <TextField size="small" value={u.email || ''} onChange={(e) => handleField(u.id, 'email', e.target.value)} disabled={u.role === 'owner' && u.id !== user.id} />
                </TableCell>
                <TableCell>
                  <Select size="small" value={Number(u.clubSlotsTier || 0)} disabled sx={{ minWidth: 160 }} MenuProps={{ disableScrollLock: true }}>
                    <MenuItem value={0}>None</MenuItem>
                    <MenuItem value={1}>Tier 1 (6–9)</MenuItem>
                    <MenuItem value={2}>Tier 2 (10–19)</MenuItem>
                    <MenuItem value={3}>Tier 3 (20–29)</MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField size="small" type="password" value={u._password || ''} onChange={(e) => handleField(u.id, '_password', e.target.value)} placeholder="Leave blank to keep" disabled={u.role === 'owner' && u.id !== user.id} />
                </TableCell>
                <TableCell>
                  <Select size="small" value={u.role || 'user'}
                    MenuProps={{ disableScrollLock: true }}
                    onChange={(e) => handleField(u.id, 'role', e.target.value)}
                    disabled={
                      // Only owners can change roles; admins can only switch between admin/user
                      (user.role !== 'owner' && user.role !== 'admin') ||
                      // Non-owners cannot target owners
                      (u.role === 'owner' && user.role !== 'owner') ||
                      // Prevent current user from changing their own role
                      (u.id === user.id)
                    }
                  >
                    {/* Only original owner can assign owner; hide for others */}
                    {user.role === 'owner' && user.username === (process.env.REACT_APP_ADMIN_USERNAME || 'admin') && (
                      <MenuItem value="owner">Owner</MenuItem>
                    )}
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                  </Select>
                </TableCell>
                <TableCell align="right">
                  {(u.role !== 'owner' || u.id === user.id) && (
                    <Button size="small" variant="contained" startIcon={<SaveIcon />} disabled={!!saving[u.id] || (u.role === 'owner' && u.id !== user.id)} onClick={() => handleSave(u)}>Save</Button>
                  )}
                  {u.role !== 'owner' && (
                    <IconButton color="error" onClick={() => handleDelete(u.id)} sx={{ ml: 1 }}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                  {(user.role === 'owner' || user.role === 'admin') && u.role !== 'owner' && (
                    <Button size="small" color="error" variant="outlined" sx={{ ml: 1 }} onClick={async () => {
                      if (!window.confirm('This will permanently wipe this user’s data (comments removed, votes anonymized, polls reassigned to Owner, clubs reassigned to Owner). Continue?')) return;
                      try {
                        await api.post(`/admin/gdpr/wipe/${u.id}`);
                        setUsers(prev => prev.filter(x => x.id !== u.id));
                        setSnack({ open: true, message: 'GDPR wipe completed', severity: 'success' });
                      } catch (e) {
                        setSnack({ open: true, message: e?.response?.data?.error || 'GDPR wipe failed', severity: 'error' });
                      }
                    }}>Wipe</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
        </Grid>
        {user?.role === 'owner' && (
        <Grid item xs={12}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Database Settings (Owner)</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
          <Button variant="outlined" onClick={async () => {
            try {
              const blob = await exportDb();
              const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/json' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `db-export-${Date.now()}.json`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch (e) {
              alert('Export failed');
            }
          }}>Export JSON</Button>
          <Button variant="outlined" component="label">
            Import JSON
            <input type="file" accept="application/json" hidden onChange={async (e) => {
              const file = e.target.files && e.target.files[0];
              if (!file) return;
              try {
                const text = await file.text();
                const json = JSON.parse(text);
                await importDb({ data: json.data || json, mode: 'upsert' });
                alert('Import completed');
              } catch (err) {
                alert('Import failed: ' + (err?.message || 'unknown error'));
              } finally {
                e.target.value = '';
              }
            }} />
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
          <Select size="small" value={dbCfg.dialect} onChange={(e) => setDbCfg({ ...dbCfg, dialect: e.target.value })} sx={{ minWidth: 160 }} MenuProps={{ disableScrollLock: true }}>
            <MenuItem value="sqlite">SQLite (local)</MenuItem>
            <MenuItem value="postgres">Postgres (remote)</MenuItem>
          </Select>
          <TextField size="small" label="DATABASE_URL (postgres)" value={dbCfg.databaseUrl || ''} onChange={(e) => setDbCfg({ ...dbCfg, databaseUrl: e.target.value })} sx={{ flex: 1 }} placeholder="postgres://user:pass@host:port/dbname" />
          <TextField size="small" label="DB_STORAGE (sqlite)" value={dbCfg.storage || ''} onChange={(e) => setDbCfg({ ...dbCfg, storage: e.target.value })} sx={{ flex: 1 }} placeholder="/app/database/movie-poll.db" />
          <Select size="small" value={dbCfg.ssl ? 'true' : 'false'} onChange={(e) => setDbCfg({ ...dbCfg, ssl: e.target.value === 'true' })} sx={{ minWidth: 140 }} MenuProps={{ disableScrollLock: true }}>
            <MenuItem value="false">SSL: disabled</MenuItem>
            <MenuItem value="true">SSL: enabled</MenuItem>
          </Select>
          <Button variant="contained" disabled={savingDb} onClick={async () => {
            setSavingDb(true);
            try {
              await saveDbConfig({ dialect: dbCfg.dialect, databaseUrl: dbCfg.databaseUrl, storage: dbCfg.storage, ssl: dbCfg.ssl });
              alert('Saved. Please restart the backend to apply changes.');
            } catch (_) {}
            setSavingDb(false);
          }}>Save</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">Note: Changes require a backend restart to take effect.</Typography>
      </Paper>
        </Grid>
        )}
        <Grid item xs={12}>
      <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="h6" gutterBottom>Clubs & Members</Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Select size="small" value={selectedClubId} displayEmpty onChange={(e) => { setSelectedClubId(e.target.value); loadMembers(e.target.value); }} sx={{ minWidth: 200 }} MenuProps={{ disableScrollLock: true }}>
            <MenuItem value=""><em>Select a club</em></MenuItem>
            {clubs.filter(c => c && c.id).map(c => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))}
          </Select>
          <TextField size="small" placeholder="Username" value={newMember.username} onChange={(e) => setNewMember({ ...newMember, username: e.target.value })} />
          <Select size="small" value={newMember.role} onChange={(e) => setNewMember({ ...newMember, role: e.target.value })} MenuProps={{ disableScrollLock: true }}>
            <MenuItem value="member">Member</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
          <Button variant="contained" disabled={!selectedClubId || !newMember.username.trim()} onClick={async () => {
            try { await addClubMember(selectedClubId, newMember.username.trim(), newMember.role); setNewMember({ username: '', role: 'member' }); loadMembers(selectedClubId); } catch (_) {}
          }}>Add</Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Platform Role</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.filter(m => m && m.userId).map(m => (
              <TableRow key={m.userId}>
                <TableCell>{m.username}</TableCell>
                <TableCell>{m.email || '-'}</TableCell>
                <TableCell>{m.platformRole}</TableCell>
                <TableCell align="right">
                  <IconButton color="error" onClick={async () => { if (window.confirm('Remove this member?')) { try { await removeClubMember(selectedClubId, m.userId); loadMembers(selectedClubId); } catch (_) {} } }}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button color="error" variant="outlined" disabled={!selectedClubId} onClick={async () => {
            if (!selectedClubId) return;
            try {
              const res = await listClubMembers(selectedClubId);
              if (res.members && res.members.length > 0) {
                alert('Club must be empty before deletion. Remove all members first.');
                return;
              }
              if (window.confirm('Delete this club? This cannot be undone.')) {
                await fetch(`${process.env.REACT_APP_API_URL || '/api'}/clubs/${selectedClubId}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '' }
                });
                setClubs(prev => prev.filter(c => c.id !== selectedClubId));
                setSelectedClubId('');
                setMembers([]);
              }
            } catch (e) {
              alert('Failed to delete club. Ensure it is empty and you have permission.');
            }
          }}>Delete Club</Button>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>Discord Webhook</Typography>
        <Stack spacing={1} sx={{ mb: 1 }}>
          <TextField size="small" label="Webhook URL" placeholder="https://discord.com/api/webhooks/..." value={webhookCfg.url} onChange={(e) => setWebhookCfg({ ...webhookCfg, url: e.target.value })} fullWidth />
          <TextField size="small" label="Default Alert Message (optional)" value={webhookCfg.message} onChange={(e) => setWebhookCfg({ ...webhookCfg, message: e.target.value })} fullWidth multiline minRows={3} />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" disabled={!selectedClubId || savingWebhook} onClick={async () => {
              if (!selectedClubId) return;
              setSavingWebhook(true);
              try {
                await putClubWebhook(selectedClubId, { webhookUrl: webhookCfg.url.trim() || null, webhookMessage: webhookCfg.message || null });
              } catch (_) {}
              setSavingWebhook(false);
            }}>Save Webhook</Button>
          </Stack>
        </Stack>
      </Paper>
        </Grid>
        <Grid item xs={12}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Random Poll Generator (Plex)</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField size="small" type="number" label="Count" value={randCfg.count} onChange={(e) => setRandCfg({ ...randCfg, count: Math.max(2, Math.min(10, Number(e.target.value)||2)) })} sx={{ width: 120 }} />
          <TextField size="small" label="Genre (optional)" value={randCfg.genre} onChange={(e) => setRandCfg({ ...randCfg, genre: e.target.value })} />
          <TextField size="small" label="Actor (optional)" value={randCfg.actor} onChange={(e) => setRandCfg({ ...randCfg, actor: e.target.value })} />
          <Select size="small" value={randCfg.leadOnly ? 'lead' : 'any'} onChange={(e) => setRandCfg({ ...randCfg, leadOnly: e.target.value === 'lead' })} sx={{ minWidth: 140 }} MenuProps={{ disableScrollLock: true }}>
            <MenuItem value="any">Any role</MenuItem>
            <MenuItem value="lead">Lead only</MenuItem>
          </Select>
          <TextField size="small" label="Director (optional)" value={randCfg.director} onChange={(e) => setRandCfg({ ...randCfg, director: e.target.value })} />
          <Select size="small" displayEmpty value={randCfg.attachClubId} onChange={(e) => setRandCfg({ ...randCfg, attachClubId: e.target.value })} sx={{ minWidth: 220 }} MenuProps={{ disableScrollLock: true }}>
            <MenuItem value=""><em>Public poll</em></MenuItem>
            {clubs.filter(c => c && c.id).map(c => (<MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>))}
          </Select>
          <Select size="small" value={randCfg.duration} onChange={(e) => setRandCfg({ ...randCfg, duration: e.target.value })} sx={{ minWidth: 140 }} MenuProps={{ disableScrollLock: true }}>
            <MenuItem value="30m">30 minutes</MenuItem>
            <MenuItem value="1h">1 hour</MenuItem>
            <MenuItem value="2h">2 hours</MenuItem>
            <MenuItem value="6h">6 hours</MenuItem>
            <MenuItem value="12h">12 hours</MenuItem>
            <MenuItem value="24h">24 hours</MenuItem>
            <MenuItem value="3d">3 days</MenuItem>
            <MenuItem value="7d">7 days</MenuItem>
          </Select>
          <TextField size="small" label="Title (optional)" value={randCfg.title} onChange={(e) => setRandCfg({ ...randCfg, title: e.target.value })} />
          <Button variant="contained" onClick={async () => {
            try {
              const payload = { count: randCfg.count };
              if (randCfg.genre.trim()) payload.genre = randCfg.genre.trim();
              if (randCfg.actor.trim()) payload.actor = randCfg.actor.trim();
              if (randCfg.leadOnly) payload.leadOnly = true;
              if (randCfg.director.trim()) payload.director = randCfg.director.trim();
              if (randCfg.title.trim()) payload.title = randCfg.title.trim();
              if (randCfg.attachClubId) payload.clubId = Number(randCfg.attachClubId);
              // Compute endDate based on duration preset
              const presets = { '30m': 30*60000, '1h': 60*60000, '2h': 2*60*60000, '6h': 6*60*60000, '12h': 12*60*60000, '24h': 24*60*60000, '3d': 3*24*60*60000, '7d': 7*24*60*60000 };
              const ms = presets[randCfg.duration] || presets['24h'];
              payload.endDate = new Date(Date.now() + ms).toISOString();
              const r = await createRandomPoll(payload);
              window.location.href = `/results/${r.id}`;
            } catch (e) {
              alert(e?.response?.data?.error || 'Failed to generate poll. Ensure Plex is connected and you have permission.');
            }
          }}>Generate</Button>
        </Stack>
      </Paper>
        </Grid>
        {user?.role === 'owner' && (
          <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Subscription Management (Owner)</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Select a user row above to adjust plan and status; changes are logged.
          </Typography>
          {(() => { const sel = users.find(x => x.id === selectedUserId) || users[0]; if (!sel) return <Typography variant="body2">No users.</Typography>; const star = (v, current) => (v === (current||'') ? ' *' : ''); return (
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" sx={{ alignSelf: 'center', mr: 1 }}>Selected: <strong>{sel.username}</strong> (ID {sel.id})</Typography>
            <Select size="small" displayEmpty value={(sel.plan) || ''} onChange={(e) => handleField(sel.id, 'plan', e.target.value)} MenuProps={{ disableScrollLock: true }}>
              <MenuItem value=""><em>None{star('', sel.plan)}</em></MenuItem>
              <MenuItem value="host">Host{star('host', sel.plan)}</MenuItem>
            </Select>
            <Select size="small" displayEmpty value={(sel.planStatus) || ''} onChange={(e) => handleField(sel.id, 'planStatus', e.target.value)} MenuProps={{ disableScrollLock: true }}>
              <MenuItem value=""><em>None{star('', sel.planStatus)}</em></MenuItem>
              <MenuItem value="active">Active{star('active', sel.planStatus)}</MenuItem>
              <MenuItem value="canceled">Canceled{star('canceled', sel.planStatus)}</MenuItem>
              <MenuItem value="past_due">Past Due{star('past_due', sel.planStatus)}</MenuItem>
            </Select>
            <Select size="small" displayEmpty value={Number(sel.clubSlotsTier || 0)} onChange={(e) => handleField(sel.id, 'clubSlotsTier', Number(e.target.value))} MenuProps={{ disableScrollLock: true }}>
              <MenuItem value={0}><em>Tier: None (Host: up to 5){star(0, sel.clubSlotsTier)}</em></MenuItem>
              <MenuItem value={1}>Tier 1 (6–9){star(1, sel.clubSlotsTier)}</MenuItem>
              <MenuItem value={2}>Tier 2 (10–19){star(2, sel.clubSlotsTier)}</MenuItem>
              <MenuItem value={3}>Tier 3 (20–29){star(3, sel.clubSlotsTier)}</MenuItem>
            </Select>
            <Button size="small" variant="outlined" onClick={async () => {
              if (!sel) return;
              try {
                await fetch(`${process.env.REACT_APP_API_URL || '/api'}/users/${sel.id}/subscription`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '' },
                  body: JSON.stringify({ plan: sel.plan || null, planStatus: sel.planStatus || null, clubSlotsTier: sel.clubSlotsTier ?? 0 })
                });
              } catch (_) {}
            }}>Apply</Button>
          </Stack> ); })()}
        </Paper>
          </Grid>
        )}
        <Grid item xs={12}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Attribution Report (Admin/Owner)</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField size="small" type="number" defaultValue={30} label="Days" id="attr-days" sx={{ width: 140 }} />
          <Button variant="outlined" onClick={async () => {
            try {
              const days = Number(document.getElementById('attr-days').value || 30);
              const data = await getAnalyticsReport(days);
              const json = JSON.stringify(data, null, 2);
              const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `analytics-report-${days}d-${Date.now()}.json`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch (e) {
              alert('Failed to fetch report');
            }
          }}>Download JSON</Button>
        </Stack>
      </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Admin;





